import { create } from "zustand";

interface ModalStackEntry {
  id: string;
  zIndex: number;
  onClose?: () => void;
  closeOnEscape: boolean;
  allowPendingChanges: boolean;
}

const BASE_Z_INDEX = 1050;
const Z_INDEX_INCREMENT = 10;

/**
 * Lock/unlock background page scroll while any modal is open. Without this,
 * scrolling inside a modal chains to the page behind it (scrolling the nav out
 * of view). Compensates for the disappearing scrollbar with padding so the
 * fixed nav doesn't shift. Ref-counted via the stack so stacked modals only
 * unlock once the last one closes.
 */
const lockBodyScroll = () => {
  if (typeof document === "undefined" || document.body.style.overflow === "hidden") return;
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
};
const unlockBodyScroll = () => {
  if (typeof document === "undefined") return;
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";
};

interface ModalStackStore {
  stack: ModalStackEntry[];
  registerModal: (
    id: string,
    onClose?: () => void,
    closeOnEscape?: boolean,
    allowPendingChanges?: boolean
  ) => number;
  unregisterModal: (id: string) => void;
  isTopmost: (id: string) => boolean;
  closeTopmost: () => void;
}

export const useModalStackStore = create<ModalStackStore>((set, get) => ({
  stack: [],

  registerModal: (id, onClose, closeOnEscape = true, allowPendingChanges = false) => {
    if (get().stack.length === 0) {
      lockBodyScroll();
    }
    const zIndex = BASE_Z_INDEX + get().stack.length * Z_INDEX_INCREMENT;
    set((state) => ({
      stack: [
        ...state.stack,
        { id, zIndex, onClose, closeOnEscape: closeOnEscape ?? true, allowPendingChanges },
      ],
    }));
    return zIndex;
  },

  unregisterModal: (id) => {
    set((state) => {
      const stack = state.stack.filter((m) => m.id !== id);
      if (stack.length === 0) {
        unlockBodyScroll();
      }
      return { stack };
    });
  },

  isTopmost: (id) => {
    const { stack } = get();
    return stack.length > 0 && stack[stack.length - 1].id === id;
  },

  closeTopmost: () => {
    const { stack } = get();
    if (stack.length > 0) {
      const topmost = stack[stack.length - 1];
      if (topmost.closeOnEscape && topmost.onClose) {
        topmost.onClose();
      }
    }
  },
}));
